import { db } from './firebase';
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  query,
  orderBy,
  limit,
  getCountFromServer,
  serverTimestamp,
} from 'firebase/firestore';
import { sendToRoss } from './artService';
import { SKETCH_GUESS_PROMPTS } from '../data/familyGames';

const PROMPTS_COL = 'gamePrompts';
const MAX_PROMPTS = 1000;
const ROSS_TIMEOUT_MS = 8000; // Increased from 3s to 8s for more reliability
const RECENT_PROMPTS_KEY = 'recent_sketch_prompts';
const MAX_RECENT = 10; // Track last 10 prompts to avoid immediate repeats

const ROSS_PROMPT_REQUEST =
  'Give me one random, creative, family-friendly thing to draw for a guessing game. Make it fun and unexpected! Just the phrase, 5-8 words max, nothing else. No quotes, no punctuation, no explanation. Examples: "a cat juggling fish" or "a robot riding a unicycle" or "grandma breakdancing"';

function getRecentPrompts() {
  try {
    const stored = localStorage.getItem(RECENT_PROMPTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function addToRecentPrompts(prompt) {
  try {
    const recent = getRecentPrompts();
    const updated = [prompt, ...recent.filter(p => p !== prompt)].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_PROMPTS_KEY, JSON.stringify(updated));
  } catch {
    // localStorage unavailable, no big deal
  }
}

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ]);
}

export async function generatePrompt(gameType = 'sketch-guess') {
  try {
    const response = await withTimeout(
      sendToRoss({ message: ROSS_PROMPT_REQUEST }),
      ROSS_TIMEOUT_MS,
    );

    const text = (response?.text || response?.message || '').trim();
    if (!text || text.length > 80) throw new Error('Invalid response');

    // Save to Firestore for future use
    await addDoc(collection(db, PROMPTS_COL), {
      text,
      gameType,
      createdAt: serverTimestamp(),
      source: 'ross',
    });

    // Track as recently used
    addToRecentPrompts(text);

    prunePrompts().catch(() => {});

    return text;
  } catch {
    return getRandomPrompt(gameType);
  }
}

export async function getRandomPrompt(gameType = 'sketch-guess') {
  const recent = getRecentPrompts();
  
  try {
    // Try to get from Firestore first (last 100 prompts for better variety)
    const q = query(
      collection(db, PROMPTS_COL),
      orderBy('createdAt', 'desc'),
      limit(100),
    );
    const snap = await getDocs(q);
    const prompts = snap.docs
      .map((d) => d.data().text)
      .filter(Boolean)
      .filter(p => !recent.includes(p)); // Exclude recently used

    if (prompts.length > 0) {
      const selected = prompts[Math.floor(Math.random() * prompts.length)];
      addToRecentPrompts(selected);
      return selected;
    }
  } catch {
    // Firestore unavailable, fall through to hardcoded
  }

  // Fallback to hardcoded list, excluding recently used
  const available = SKETCH_GUESS_PROMPTS.filter(p => !recent.includes(p));
  const pool = available.length > 0 ? available : SKETCH_GUESS_PROMPTS;
  const selected = pool[Math.floor(Math.random() * pool.length)];
  addToRecentPrompts(selected);
  return selected;
}

async function prunePrompts() {
  const colRef = collection(db, PROMPTS_COL);
  const countSnap = await getCountFromServer(colRef);
  const total = countSnap.data().count;

  if (total <= MAX_PROMPTS) return;

  const excess = total - MAX_PROMPTS;
  const oldestQuery = query(colRef, orderBy('createdAt', 'asc'), limit(excess));
  const oldestSnap = await getDocs(oldestQuery);

  const deletes = oldestSnap.docs.map((d) => deleteDoc(d.ref));
  await Promise.all(deletes);
}
